import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { MatrixCallContext } from '../../../../MatrixCallProvider';
import { ROLE } from '../../../../shared/constants/common';
import { getUserRole } from '../../../../shared/utils/function';
import { DEFAULT_AVATAR } from '../../../../shared/utils/matrix/matrixRoomUtils';
import PurchaseCreditsModal from '../../../Modals/Message/PurchaseCreditsModal';
import { useMatrixRoomInfo } from '../../../../shared/utils/matrix/useMatrixRoomInfo';
import { fetchMatrixRooms } from '../../../../shared/actions/matrixAction';
import { fetchWalletBalanceAction } from '../../../../shared/actions/creditAction';
import { getMatrixRoomsArray } from '../../../../shared/utils/chatUtil';
import {
  applyPostCallBillingDurationAdjustments,
  buildDeductCallCreditPayload,
  creditsToUsdRounded,
  formatCreditsValue,
  getCallRequestPricingDetails,
  hangupPartyForCallBilling,
  MIN_BILLED_CALL_DURATION_SEC,
  subscriberHasMinimumCreditsForCall,
} from './utils/callFormatUtils';
import {
  CALL_MEDIA_TYPE,
  CREDITS_PER_USD,
  getCallKindFromCallUiType,
  navigateToCallRoute,
} from './constant';
import {
  formatCallDurationSummary,
  formatCallSummaryDisplayName,
  humanizeCallEndedReason,
  parseCallDurationSec,
  parseCallSummaryBooleanFlag,
} from './utils/callSummaryUtils';

/** Subscriber summary: show credit requirement only when creator has a real minimum (flat block or multi-minute floor). */
const shouldShowSubscriberMinimumCallCopy = (pricing) => {
  if (!pricing?.ok) return false;
  if (pricing.pricingModel === 'flat_rate') return true;
  return pricing.minDurationMins > 1;
};

const CallSummary = ({ embedded = false, summaryOverride = null, onClose = null }) => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { incomingCall } = useContext(MatrixCallContext);
  const walletBalance = useSelector((state) => state.credit?.walletBalance);
  const walletBalanceLoading = useSelector((state) => state.credit?.walletBalanceLoading);
  const currentUser = useSelector((state) => state.user?.user);
  const matrixRooms = useSelector((state) => state.matrix?.rooms || {});
  const matrixLoading = useSelector((state) => state.matrix?.loading);
  const hasRoomsLoaded = useSelector((state) => state.matrix?.hasRoomsLoaded);
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  const searchParams = new URLSearchParams(location?.search || '');
  const summaryState = summaryOverride || location?.state || {};

  const formatUsd = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);

  const queryValue = (key) => (summaryOverride ? null : searchParams.get(key));

  const summaryData = {
    isCreator:
      parseCallSummaryBooleanFlag(summaryState?.isCreator) ??
      parseCallSummaryBooleanFlag(queryValue('isCreator')),
    callDurationSec: parseCallDurationSec(
      summaryState?.callDurationSec,
      queryValue('callDurationSec')
    ),
    userId: summaryState?.userId || null,
    roomId: summaryState?.roomId || queryValue('roomId') || null,
    callUiType: summaryState?.callUiType || queryValue('callUiType') || '',
    endedReason: summaryState?.endedReason || queryValue('endedReason') || null,
    hangupParty: summaryState?.hangupParty || queryValue('hangupParty') || null,
    billedCredits: (() => {
      const raw =
        summaryState?.billedCredits !== undefined && summaryState?.billedCredits !== null
          ? summaryState.billedCredits
          : queryValue('billedCredits');
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    })(),
    tipUsd: (() => {
      const raw =
        summaryState?.tipUsd !== undefined && summaryState?.tipUsd !== null
          ? summaryState.tipUsd
          : summaryState?.tipCredits !== undefined && summaryState?.tipCredits !== null
            ? summaryState.tipCredits
            : queryValue('tipUsd') ??
            queryValue('tipCredits'); // legacy key: value was always USD
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    })(),
    hasSessionBilling:
      (summaryState?.billedCredits !== undefined && summaryState?.billedCredits !== null) ||
      Boolean(queryValue('billedCredits')) ||
      summaryState?.billingDurationFinal === true,
    callPricing:
      summaryState?.callPricing && typeof summaryState.callPricing === 'object'
        ? summaryState.callPricing
        : null,
  };

  const role = getUserRole();
  const isCreator =
    summaryData?.isCreator !== undefined
      ? Boolean(summaryData.isCreator)
      : role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT;

  useEffect(() => {
    if (!summaryData?.roomId) return;
    if (hasRoomsLoaded) return;
    if (matrixLoading) return;
    dispatch(fetchMatrixRooms());
  }, [dispatch, hasRoomsLoaded, matrixLoading, summaryData?.roomId]);

  useEffect(() => {
    if (isCreator) return;
    const subscriberId = currentUser?._id || currentUser?.id;
    if (!subscriberId) return;
    dispatch(fetchWalletBalanceAction(subscriberId));
  }, [dispatch, isCreator, currentUser?._id, currentUser?.id]);

  const matrixRoomsArray = useMemo(() => getMatrixRoomsArray(matrixRooms), [matrixRooms]);
  const roomMetadata = useMemo(
    () => matrixRoomsArray.find((room) => room?.roomid === summaryData?.roomId) || null,
    [matrixRoomsArray, summaryData?.roomId]
  );
  const resolvedUserId = summaryData?.userId || roomMetadata?.otherUser?._id || null;
  const { destUserInfo } = useMatrixRoomInfo(resolvedUserId);

  const callKind = getCallKindFromCallUiType(summaryData?.callUiType);
  const endedCallLabel =
    callKind === CALL_MEDIA_TYPE.VOICE ? 'Voice call ended with' : 'Video call ended with';
  const endedCallIconClass =
    callKind === CALL_MEDIA_TYPE.VOICE ? 'fa-solid fa-phone-slash' : 'fa-solid fa-video';

  const tipEarningsUsd = isCreator ? Math.max(0, Number(summaryData.tipUsd) || 0) : 0;

  const billingDurationSec = useMemo(() => {
    const raw = Math.max(0, Number(summaryData.callDurationSec) || 0);
    if (!isCreator) return raw;
    // Redirect already applied flat-rate / insufficient-credit snaps with the call's rate profile.
    if (summaryData.hasSessionBilling) return raw;
    return applyPostCallBillingDurationAdjustments(raw, {
      creatorInfo: currentUser,
      callKind,
      endedReason: summaryData.endedReason,
    });
  }, [
    isCreator,
    summaryData.callDurationSec,
    summaryData.endedReason,
    summaryData.hasSessionBilling,
    callKind,
    currentUser,
  ]);

  /**
   * Prefer credits computed on the call screen with the same rate profile as live "Total collected"
   * (including one-off outgoing overrides). Recalculating from Redux currentUser alone caused
   * live vs summary mismatches when the call used a different rate than the saved profile.
   */
  const durationEarningsUsd = useMemo(() => {
    if (!isCreator) return 0;
    const durationSec = billingDurationSec;
    if (durationSec < MIN_BILLED_CALL_DURATION_SEC) return 0;

    if (summaryData.hasSessionBilling) {
      return creditsToUsdRounded(summaryData.billedCredits);
    }

    const creatorId = currentUser?._id || currentUser?.id;
    if (creatorId && currentUser) {
      const payload = buildDeductCallCreditPayload(
        creatorId,
        currentUser,
        callKind,
        durationSec,
        {
          hangupParty: hangupPartyForCallBilling(true, summaryData.hangupParty),
          endedReason: summaryData.endedReason,
        }
      );
      if (payload?.credit != null) {
        const n = Number(payload.credit);
        if (Number.isFinite(n)) return creditsToUsdRounded(n);
      }
    }
    return creditsToUsdRounded(summaryData.billedCredits);
  }, [
    isCreator,
    billingDurationSec,
    summaryData.billedCredits,
    summaryData.hasSessionBilling,
    summaryData.hangupParty,
    summaryData.endedReason,
    callKind,
    currentUser,
  ]);

  const subscriberCallPricing = useMemo(
    () => (isCreator ? null : getCallRequestPricingDetails(destUserInfo, callKind)),
    [isCreator, destUserInfo, callKind]
  );
  const creatorCallPricing = useMemo(() => {
    if (!isCreator) return null;
    const snap = summaryData.callPricing;
    if (
      snap &&
      (snap.pricingModel === 'per_minute' || snap.pricingModel === 'flat_rate') &&
      Number.isFinite(Number(snap.usdPerMinute))
    ) {
      return {
        ok: true,
        pricingModel: snap.pricingModel,
        usdPerMinute: Number(snap.usdPerMinute),
        amountUsd: Number(snap.amountUsd),
        minDurationMins: Number(snap.minDurationMins) || 0,
        ratePerMinuteCredits: Number(snap.usdPerMinute) * CREDITS_PER_USD,
      };
    }
    return getCallRequestPricingDetails(currentUser, callKind);
  }, [isCreator, currentUser, callKind, summaryData.callPricing]);

  const creditsNumeric = Number(walletBalance?.credits);

  /** Show subscriber minimum call copy when:
   * 1. User is not a creator
   * 2. Subscriber call pricing is available
   * 3. Wallet balance is not loading
   * 4. Credits numeric is finite
   * 5. Credits numeric is less than subscriber call pricing minimum cost credits
   */
  const showSubscriberMinimumCallCopy =
    !isCreator &&
    shouldShowSubscriberMinimumCallCopy(subscriberCallPricing) &&
    !walletBalanceLoading &&
    Number.isFinite(creditsNumeric) &&
    creditsNumeric < subscriberCallPricing.minCostCredits;

  const apiDisplayName = destUserInfo?.username || roomMetadata?.otherUser?.username || null;
  const apiAvatar =
    destUserInfo?.profile_picture ||
    destUserInfo?.profilePicture ||
    roomMetadata?.otherUser?.profile_picture ||
    roomMetadata?.otherUser?.profilePicture ||
    null;
  const opponentDisplayName = formatCallSummaryDisplayName(
    apiDisplayName || summaryState?.opponentDisplayName
  );
  const opponentAvatar = apiAvatar || summaryState?.opponentAvatar || DEFAULT_AVATAR;
  const durationLabel = formatCallDurationSummary(
    isCreator ? billingDurationSec : summaryData.callDurationSec
  );

  const endedReasonLabel = humanizeCallEndedReason(
    summaryData?.endedReason,
    summaryData?.hangupParty,
    opponentDisplayName,
    isCreator
  );

  const creditsRemaining = walletBalance?.credits ?? 0;
  const canCallAgainNow =
    Boolean(summaryData?.roomId) &&
    subscriberHasMinimumCreditsForCall(subscriberCallPricing, creditsNumeric, walletBalanceLoading);
  const creatorRatePerMinuteUsd =
    creatorCallPricing?.ok && Number.isFinite(creatorCallPricing?.usdPerMinute)
      ? Number(creatorCallPricing.usdPerMinute)
      : creatorCallPricing?.ok && Number.isFinite(creatorCallPricing?.ratePerMinuteCredits)
        ? creditsToUsdRounded(creatorCallPricing.ratePerMinuteCredits)
        : 0;

  const showCreatorRateRow =
    creatorCallPricing?.ok &&
    (durationEarningsUsd > 0 || tipEarningsUsd > 0 || billingDurationSec > 0);

  const handleClose = () => {
    if (embedded && typeof onClose === 'function') {
      onClose();
      return;
    }
    if (resolvedUserId) {
      navigate.toChat(resolvedUserId, {
        state: summaryData?.roomId ? { preferredRoomId: summaryData.roomId } : undefined,
      });
    } else {
      navigate.toMessages();
    }
  };

  const handleCallAgain = () => {
    const roomId = summaryData?.roomId;
    const pricing = subscriberCallPricing;

    if (isCreator && roomId) {
      void navigateToCallRoute(navigate, callKind, { roomId, callType: 'outgoing' });
      return;
    }

    if (canCallAgainNow) {
      void navigateToCallRoute(navigate, callKind, { roomId, callType: 'outgoing' });
      return;
    }

    if (walletBalanceLoading && pricing?.ok) {
      return;
    }

    if (
      pricing?.ok &&
      Number.isFinite(creditsNumeric) &&
      creditsNumeric < pricing.minCostCredits
    ) {
      setShowPurchaseCreditsModal(true);
      return;
    }

    if (summaryData?.userId) {
      navigate.toChat(summaryData.userId, {
        state: roomId ? { preferredRoomId: roomId } : undefined,
      });
      return;
    }
    navigate.toMessages();
  };

  const handleAddCredits = () => {
    setShowPurchaseCreditsModal(true);
  };

  useEffect(() => {
    if (!incomingCall) return;
    if (embedded && typeof onClose === 'function') onClose();
  }, [incomingCall, embedded, onClose]);

  if (incomingCall) return null;

  return (
    <>
      <div
        className={`call-wrapper call-wrapper_backdrop_blur d-flex flex-column align-items-center justify-center py-4${embedded ? ' call-summary-popup-overlay' : ''}`}
      >
        <div
          className={`call-card call-card2 position-relative text-center p-4 rounded-4${embedded ? ' call-summary-popup-card' : ''}`}
        >
          <span className="cm_close_btn" onClick={handleClose} style={{ cursor: 'pointer' }}>
            <i className="fa-solid fa-xmark" />
          </span>

          <div className="call_summary_user_img">
            <img src={opponentAvatar} alt={opponentDisplayName} />
          </div>

          <h5 className="text-white mb-2 mt-4">
            {endedCallLabel} <span className="fw-bold">{opponentDisplayName}</span>{' '}
            <i className={endedCallIconClass} />
          </h5>

          {endedReasonLabel && (
            <p className="text-light mb-3">
              Reason: <span className="fw-bold">{endedReasonLabel}</span>
            </p>
          )}

          {isCreator ? (
            <>
              <div className="earn-box mb-4">
                <h1 className="fw-bold text-white">
                  {formatUsd(durationEarningsUsd)}
                </h1>
                <p className="text-light mb-0">Earnings from call duration</p>
              </div>

              <div className="d-flex w-full gap-4 mb-4">
                <div className="info-box p-3 w-full">
                  <h4 className="text-white mb-1">{formatUsd(tipEarningsUsd)}</h4>
                  <span className="text-light small">Tip earnings</span>
                </div>

                <div className="info-box p-3 w-full">
                  <h4 className="text-white mb-1">{durationLabel}</h4>
                  <span className="text-light small">Duration</span>
                </div>
              </div>

              {showCreatorRateRow && (
                <div className="info-box p-3 w-full mb-4 cm_call_summary_rate_row">
                  <h4 className="text-white mb-0">
                    {creatorCallPricing.pricingModel === 'flat_rate'
                      ? `Sub paid ${formatUsd(creatorCallPricing.amountUsd)} per ${creatorCallPricing.minDurationMins} ${creatorCallPricing.minDurationMins === 1 ? 'minute' : 'minutes'}`
                      : `Sub paid ${formatUsd(creatorRatePerMinuteUsd)} per minute`}
                  </h4>
                </div>
              )}

              <button
                type="button"
                className="w-full cm_call_again_btn cm_call_again_btn_active cm_call_again_btn_creator"
                onClick={handleCallAgain}
                disabled={!summaryData?.roomId}
                title={!summaryData?.roomId ? 'No room available to call again.' : undefined}
              >
                Call Again
              </button>
            </>
          ) : (
            <>
              {showSubscriberMinimumCallCopy && subscriberCallPricing?.ok && (
                <div className="text-light mb-4">
                  <p className="mb-2">
                    You need a minimum of {formatCreditsValue(subscriberCallPricing.minCostCredits)} credits
                    to start another call with this creator.
                  </p>
                </div>
              )}

              <div className="earn-box mb-4">
                <h1 className="fw-bold">{durationLabel}</h1>
                <p className="text-light mb-0">Call Duration</p>
              </div>

              <div className="d-flex w-full gap-4 mb-4">
                <div className="info-box p-3 w-full cm_call_summary_tile">
                  <h4 className="text-white mb-3">
                    <i className="fa-solid fa-coins me-2" />
                  </h4>
                  <span className="text-light small">
                    {formatCreditsValue(creditsRemaining)} credits
                  </span>
                </div>

                <button
                  type="button"
                  className="info-box p-3 w-full text-white cm_call_summary_tile cm_call_summary_tile_add_credits"
                  onClick={handleAddCredits}
                >
                  <h4 className="mb-3">
                    <i className="fa-solid fa-credit-card" />
                  </h4>
                  <span className="text-light small">Add Credits</span>
                </button>
              </div>

              <button
                type="button"
                className={`w-full cm_call_again_btn${canCallAgainNow ? ' cm_call_again_btn_active' : ''}`}
                onClick={handleCallAgain}
                disabled={Boolean(subscriberCallPricing?.ok && walletBalanceLoading)}
                title={
                  subscriberCallPricing?.ok && walletBalanceLoading
                    ? 'Checking your credit balance…'
                    : undefined
                }
              >
                Call Again
              </button>
            </>
          )}
        </div>
      </div>
      <PurchaseCreditsModal
        isOpen={showPurchaseCreditsModal}
        onClose={() => setShowPurchaseCreditsModal(false)}
      />
    </>
  );
};

export default CallSummary;
